import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Link as LinkIcon, Brain } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ThreatOption {
  id: string;
  threat_title: string;
  client_site_name: string;
  risk_level: string;
  incident_history: string | null;
  recommended_mitigations: string | null;
  access_points: string | null;
  lighting_conditions: string | null;
  camera_coverage: string | null;
  client_concerns: string | null;
  known_risks: string[] | null;
  hours_of_operation: string | null;
  property_type: string;
  ai_generated_assessment: string | null;
  state: string | null;
}

export interface ThreatImportData {
  threatId: string;
  riskLevel: string;
  threatAssessmentText: string;
  recommendedMitigations: string;
  notes: string;
  aiGeneratedAssessment: string | null;
  state: string | null;
}

interface ThreatSelectorProps {
  selectedThreatId: string | null;
  onSelect: (data: ThreatImportData | null) => void;
  hasUserEdits: boolean;
}

const riskColors: Record<string, string> = {
  low: "bg-green-500/10 text-green-500 border-green-500/30",
  moderate: "bg-yellow-500/10 text-yellow-500 border-yellow-500/30",
  high: "bg-orange-500/10 text-orange-500 border-orange-500/30",
  critical: "bg-destructive/10 text-destructive border-destructive/30",
};

const ThreatSelector = ({ selectedThreatId, onSelect, hasUserEdits }: ThreatSelectorProps) => {
  const { user } = useAuth();
  const [threats, setThreats] = useState<ThreatOption[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingThreatId, setPendingThreatId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("threat_assessments")
      .select("id, threat_title, client_site_name, risk_level, incident_history, recommended_mitigations, access_points, lighting_conditions, camera_coverage, client_concerns, known_risks, hours_of_operation, property_type, ai_generated_assessment, state")
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data) setThreats(data as ThreatOption[]);
      });
  }, [user]);

  const buildImportData = (threat: ThreatOption): ThreatImportData => {
    const parts: string[] = [];
    if (threat.access_points) parts.push(`Access Points: ${threat.access_points}`);
    if (threat.lighting_conditions) parts.push(`Lighting Conditions: ${threat.lighting_conditions}`);
    if (threat.camera_coverage) parts.push(`Camera Coverage: ${threat.camera_coverage}`);
    if (threat.incident_history) parts.push(`Incident History: ${threat.incident_history}`);
    if (threat.known_risks?.length) parts.push(`Known Risks: ${threat.known_risks.join(", ")}`);
    if (threat.client_concerns) parts.push(`Client Concerns: ${threat.client_concerns}`);

    const noteParts: string[] = [];
    if (threat.hours_of_operation) noteParts.push(`Hours of Operation: ${threat.hours_of_operation}`);

    return {
      threatId: threat.id,
      riskLevel: threat.risk_level,
      threatAssessmentText: parts.join("\n\n"),
      recommendedMitigations: threat.recommended_mitigations || "",
      notes: noteParts.join("\n"),
      aiGeneratedAssessment: threat.ai_generated_assessment || null,
      state: threat.state || null,
    };
  };

  const handleChange = (value: string) => {
    if (value === "none") {
      onSelect(null);
      return;
    }
    if (hasUserEdits) {
      setPendingThreatId(value);
      setShowConfirm(true);
      return;
    }
    applySelection(value);
  };

  const applySelection = (threatId: string) => {
    const threat = threats.find((t) => t.id === threatId);
    if (threat) {
      onSelect(buildImportData(threat));
    }
  };

  const confirmOverwrite = () => {
    if (pendingThreatId) applySelection(pendingThreatId);
    setShowConfirm(false);
    setPendingThreatId(null);
  };

  if (threats.length === 0) {
    return (
      <Card className="border-border bg-card shadow-card">
        <CardContent className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="font-semibold text-foreground text-sm">No Threat Assessments</p>
              <p className="text-xs text-muted-foreground">Create a threat assessment first to import data.</p>
            </div>
          </div>
          <Link to="/threat-builder">
            <Button variant="outline" size="sm">Create Threat</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const selected = threats.find((t) => t.id === selectedThreatId);

  return (
    <>
      <Card className="border-border bg-card shadow-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-foreground text-base">
            <LinkIcon className="h-5 w-5 text-primary" />
            Select Existing Threat Assessment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={selectedThreatId || "none"} onValueChange={handleChange}>
            <SelectTrigger className="bg-secondary border-border text-foreground">
              <SelectValue placeholder="No threat selected — proceed manually" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="none">No threat selected — proceed manually</SelectItem>
              {threats.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.threat_title} — {t.client_site_name}
                  {t.ai_generated_assessment ? " ✦" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selected && (
            <div className="p-3 rounded-lg bg-secondary/50 text-sm space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Risk Level:</span>
                <Badge variant="outline" className={riskColors[selected.risk_level] || ""}>
                  {selected.risk_level.charAt(0).toUpperCase() + selected.risk_level.slice(1)}
                </Badge>
                {selected.ai_generated_assessment && (
                  <Badge className="bg-primary/10 text-primary border-primary/30 text-xs">
                    <Brain className="h-3 w-3 mr-1" />
                    AI Enhanced
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground">
                Property: <span className="text-foreground capitalize">{selected.property_type}</span>
              </p>
              {selected.known_risks?.length ? (
                <div className="flex flex-wrap gap-1">
                  {selected.known_risks.map((r) => (
                    <Badge key={r} variant="secondary" className="text-xs">{r}</Badge>
                  ))}
                </div>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {selected.ai_generated_assessment
                  ? "This threat includes an AI-generated assessment that will enhance the proposal with detailed threat analysis, risk matrix, and crime data."
                  : "Selecting this will auto-fill threat data into your proposal. You can edit any field afterward."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Overwrite Existing Data?</AlertDialogTitle>
            <AlertDialogDescription>
              You have made edits to the proposal. Selecting a different threat will overwrite the threat assessment, risk level, and mitigation fields. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOverwrite}>Overwrite</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ThreatSelector;
